import React, { useState } from 'react'
import rangeParser from 'parse-numeric-range'
import Highlight, { defaultProps, Prism } from 'prism-react-renderer'
import theme from 'prism-react-renderer/themes/dracula'
import { Box, Button, Typography } from '@mui/material'

import { ADDITIONAL_LANGUAGES, LANGUAGE_TYPE, getBadgeColor } from './constants'

import { copyToClipboard } from '~/utils/common'

const globalObject = typeof global !== 'undefined' ? global : window
globalObject.Prism = Prism

ADDITIONAL_LANGUAGES.forEach(lang =>
  // eslint-disable-next-line import/no-dynamic-require, global-require
  require(`prismjs/components/prism-${lang}`)
)

const calculateLinesToHighlight = (meta: string) => {
  const RE = /{([\d,-]+)}/

  if (RE.test(meta)) {
    const strlineNumbers = RE.exec(meta)
    const lineNumbers = rangeParser(strlineNumbers ? strlineNumbers[1] : '')
    return (index: number) => lineNumbers.includes(index + 1)
  }

  return () => false
}

type CodeProps = {
  language: LANGUAGE_TYPE
  codeString: string
  highlight: string
}

export const Code = ({ language, codeString, highlight }: CodeProps) => {
  const [copied, setCopied] = useState(false)

  const shouldHighlightLine = calculateLinesToHighlight(highlight)

  const handleClick = () => {
    setCopied(true)
    copyToClipboard(codeString)

    setTimeout(() => {
      setCopied(false)
    }, 4000)
  }

  return (
    <Box
      sx={{
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        overflowX: 'auto'
      }}
    >
      <Highlight
        {...defaultProps}
        code={codeString}
        language={language}
        theme={theme}
      >
        {({
          className, style, tokens, getLineProps, getTokenProps 
        }) => (
          <Box
            component='pre'
            className={className}
            style={style}
            sx={{
              borderRadius: 2,
              whiteSpace: 'pre'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box
                sx={{
                  height: 'fit-content',
                  bgcolor: getBadgeColor(language).bgcolor,
                  borderRadius: '0 0 4px 4px',
                  color: getBadgeColor(language).color || 'text.primary',
                  fontSize: 12,
                  lineHeight: 1,
                  ml: 2,
                  px: 1,
                  py: 0.5,
                  visibility: language ? 'visible' : 'hidden'
                }}
              >
                {language?.toUpperCase()}
              </Box>
              <Button
                disabled={copied}
                onClick={handleClick}
                sx={{
                  bgcolor: 'text.disabled',
                  borderRadius: '0 8px 0 4px',
                  color: 'primary.light',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  lineHeight: 1,
                  minWidth: 'fit-content',
                  px: 1.25,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'secondary.main'
                  },
                  '&.Mui-disabled': {
                    color: 'primary.light'
                  }
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
            <Box
              component='code'
              sx={{
                display: 'grid',
                fontFamily: 'inherit',
                fontSize: 14,
                lineHeight: '22px',
                overflowX: 'auto',
                pb: 2,
                pt: 1,
                px: 2,
                wordBreak: 'break-all'
              }}
            >
              {tokens.map((line, index) => (
                <Box
                  key={index}
                  {...getLineProps({ line })}
                  sx={{
                    ...(shouldHighlightLine(index) && {
                      bgcolor: 'text.disabled',
                      mx: -2,
                      px: 2
                    })
                  }}
                >
                  {line.map((token, key) => (
                    <Typography
                      {...getTokenProps({ token, key })}
                      component='span'
                      key={key}
                      sx={{
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit'
                      }}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Highlight>
    </Box>
  )
}
